import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import AxiosInstance from "../../../services/Api";
import "./Index.css";

export default function StudentProfile({
  user,
  title,
  isParent,
  studentDetails,
}) {
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);
  const clientData = user?.client_data?.[0] || {};
  const student_name = user.name;
  // For parent, use first child from studentDetails
  const studentData =
    isParent && studentDetails?.length > 0 ? studentDetails[0] : null;

  const studentName = isParent
    ? studentData?.student_name || ""
    : `${student_name || ""}`.trim();

  const studentId = isParent
    ? studentData?.student_id || ""
    : clientData?.student_id || clientData?.student_id || "";

  const className = isParent
    ? studentData?.class || ""
    : clientData?.class_name || clientData?.class || "";

  const section = isParent
    ? studentData?.section_id || ""
    : clientData?.student_section || clientData?.student_section || "";

  // blood_group may be null when the record stores the text directly
  // (join to domain master finds nothing) — fall back to blood_group_id
  const cookieBloodGroup = isParent
    ? studentData?.blood_group || studentData?.blood_group_id || ""
    : clientData?.blood_group || clientData?.blood_group_id || "";

  // The login cookie is a snapshot and may not carry the blood group at
  // all — for students, fetch it live from the profile API when missing
  const [liveBloodGroup, setLiveBloodGroup] = useState("");

  useEffect(() => {
    if (isParent || cookieBloodGroup || !user?.id) return;

    let cancelled = false;
    AxiosInstance.post(`/groupCockpit/profile/${user.id}`, {
      user_type: "student",
    })
      .then((response) => {
        if (cancelled) return;
        const p = response?.data?.data || response?.data || {};
        setLiveBloodGroup(p.blood_group || p.blood_group_id || "");
      })
      .catch((err) => {
        console.error("Error fetching blood group:", err);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParent, cookieBloodGroup, user?.id]);

  const bloodGroup = cookieBloodGroup || liveBloodGroup;

  return (
    <div className="student-profile-card">
      {/* Header */}
      <div className="student-profile-header">
        <h2>{title}</h2>
      </div>

      {/* My Child label for Parent */}
      {isParent && (
        <div className="student-profile-child-label">
          <span className="child-icon">👨‍👧</span>
          <span>{t.myChild || "My Child"}</span>
        </div>
      )}

      {/* Avatar */}
      <div className="student-profile-avatar">
        <div className="student-avatar-circle">
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <circle
              cx="30"
              cy="30"
              r="30"
              fill="var(--color-brand-orange, #ff6b35)"
            />
            <circle
              cx="30"
              cy="22"
              r="10"
              fill="var(--color-brand-orange-light, #f7931e)"
            />
            <path
              d="M10 52c0-11.046 8.954-20 20-20s20 8.954 20 20"
              fill="var(--color-brand-orange-light, #f7931e)"
            />
          </svg>
        </div>
      </div>

      {/* Name */}
      <div className="student-profile-name">{studentName}</div>

      {/* Details */}
      <div className="student-profile-details">
        <div className="student-detail-row">
          <span className="student-detail-label">
            {t.studentName || "Student Name"}
          </span>
          <span className="student-detail-value">{studentName}</span>
        </div>
        <div className="student-detail-row">
          <span className="student-detail-label">
            {t.studentId || "Student id"}
          </span>
          <span className="student-detail-value">{studentId}</span>
        </div>
        <div className="student-detail-row">
          <span className="student-detail-label">{t.class || "Class"}</span>
          <span className="student-detail-value">{className}</span>
        </div>
        <div className="student-detail-row">
          <span className="student-detail-label">{t.section || "Section"}</span>
          <span className="student-detail-value">{section}</span>
        </div>
        <div className="student-detail-row">
          <span className="student-detail-label">
            {t.bloodGroup || "Blood Group"}
          </span>
          <span className="student-detail-value">{bloodGroup}</span>
        </div>
      </div>
    </div>
  );
}
